import { useState, useEffect, useRef } from "react";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import { client } from "../../sdkAdapter";
import Swal from "sweetalert2";

interface UserMetaCardProps {
  user: any;
  onUpdate: () => void;
}

export default function UserMetaCard({ user, onUpdate }: UserMetaCardProps) {
  const { isOpen, openModal, closeModal } = useModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    username: "",
    name: "",
    email: "",
    phone: "",
    bio: "",
  });

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || "",
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        bio: user.bio || "",
      });
    }
  }, [user, isOpen]);

  // ================= SAVE PROFILE =================
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Full Name and Email are required.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    setSaving(true);

    try {
      await client.postApiAccountsUserUpdateId({
        id: String(user.id),
        postApiAccountsUserUpdateIdRequest: {
          id: Number(user.id),
          first_name: formData.name.split(" ")[0],
          last_name: formData.name.split(" ").slice(1).join(" "),
          email: formData.email,
        },
      } as any);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Profile Updated successfully',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
      });

      onUpdate();
      closeModal();
    } catch (err: any) {
      console.error("Failed to update profile", err);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          err.response?.data?.message ||
          "Something went wrong while updating your profile.",
        confirmButtonColor: "#EF4444",
      });
    } finally {
      setSaving(false);
    }
  };

  // ================= IMAGE CLICK =================
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  // ================= IMAGE UPLOAD =================
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("folder", "users");

      const res = (await client.postApiMediaFileUpload(
        { postApiMediaFileUploadRequest: form as any },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )) as any;

      const uploadedFile = res.data?.data || res.data;

      if (uploadedFile?.id) {
        await client.postApiAccountsUserUpdateId({
          id: String(user.id),
          postApiAccountsUserUpdateIdRequest: {
            id: Number(user.id),
            image_id: String(uploadedFile.id),
          },
        } as any);

        await Swal.fire({
          icon: "success",
          title: "Profile Image Updated!",
          showConfirmButton: false,
          timer: 1500,
        });

        onUpdate();
      }
    } catch (err: any) {
      console.error("Failed to upload image:", err);

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          err.response?.data?.message ||
          "Failed to upload image. Please try again.",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ================= IMAGE URL =================
  const getImageUrl = () => {
    const img = user.image || user.avatar;

    let src =
      typeof img === "object" && img !== null
        ? img.path || img.url
        : img;

    if (src && typeof src === "string") {
      if (src.startsWith("http")) return src;
      const apiBase =
        import.meta.env.VITE_API_BASE || "http://127.0.0.1:8000";
      return `${apiBase}/${src.replace(/^\//, "")}`;
    }

    return "/images/user/owner.jpg";
  };

  if (!user) return null;

  return (
    <>
      <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
            <div className="relative group">
              <div className="w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800">
                <img
                  src={getImageUrl()}
                  alt="user"
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                onClick={handleImageClick}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                {uploading ? (
                  <svg
                    className="w-6 h-6 text-white animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                  </svg>
                ) : (
                  <span className="text-white text-sm">Change</span>
                )}
              </div>

              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>

            <div>
              <h4 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white">
                {user.username}
              </h4>
              <p className="text-sm text-gray-500">
                {user.email}
              </p>
            </div>
          </div>

          <button
            onClick={openModal}
            className="rounded-full border px-4 py-2 text-sm"
          >
            Edit
          </button>
        </div>
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-md w-full mx-auto">
        <form onSubmit={handleSave} className="p-6">
          <h4 className="text-xl font-semibold mb-4">
            Edit Personal Information
          </h4>

          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e: any) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Email Address</Label>
              <Input
                type="text"
                value={formData.email}
                onChange={(e: any) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={closeModal}
            >
              Close
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}